import React from "react";

import { Button } from "../../../../Shared/Buttons/Button";
import { dmnIdentity } from "../../../../../utils/dmnIdentity";
import { getEnvironmentProps } from "../../../../../utils/environment";
import dayjs from "dayjs";
import { useDiaryContext } from "../../../../../hooks/useDiaryContext";
import { ToggleButton } from "../../../../Shared/Buttons/ToggleButton";
import { ChevronRight } from "lucide-react";
import { document } from "@workspace/mobile";
import { DownloadType } from "../DownloadPrintModal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../../../../ui/select";
import {
  errorHandler,
  apiFetchNotOkHandler
} from "../../../../../APIs/errorHandler";
/**
 * DownloadCustomRunSheetContent Props
 */
type DownloadCustomRunSheetContentProps = {
  onClose?: () => void;
  onBack?: () => void;
  onSelectDownloadType?: (type: DownloadType) => void;
};

/**
 * DownloadCustomRunSheetContent Modal
 *
 * @param param0
 * @returns
 */
export const DownloadCustomRunSheetContent = ({
  onBack,
  onClose,
  onSelectDownloadType
}: DownloadCustomRunSheetContentProps) => {
  const { venueConfig, date } = useDiaryContext();
  const [fileType, setFileType] = React.useState<string>("pdf");

  const handleSelectCustomRunSheet = () => {
    onSelectDownloadType?.("ShortRunSheet");
  };

  const handleBack = () => {
    onBack?.();
  };

  const handleDownload = async () => {
    const token = await dmnIdentity.getAccessToken();
    const environmentVars = getEnvironmentProps(token);
    const venueId = venueConfig?._id;
    const formattedDate = dayjs(date).format("YYYY-MM-DD");

    const url = new URL(
      `${environmentVars?.COLLINS_API_BASE_URL}/run-sheets/short/${venueId}/${formattedDate}`
    );
    url.searchParams.set("output", fileType);
    url.searchParams.set("status", "in_progress,complete");

    try {
      const request = await fetch(`${url}`, {
        headers: {
          authorization: `Bearer ${token}`
        },
        method: "GET"
      });

      if (!request.ok) {
        apiFetchNotOkHandler(request, "Error downloading run sheet");
        return;
      }

      const arrBuffer = await request.arrayBuffer();
      const file = new Blob([arrBuffer], { type: "application/pdf" });
      const fileReader = new FileReader();
      fileReader.onloadend = async () => {
        const result = fileReader.result as string;
        const [, mimeType, , data] = result.split(/[:;,]+/);
        const fileName = `run-sheet.${fileType === "pdf" ? "pdf" : "csv"}`;
        document.open({ mimeType, data, fileName });
      };

      fileReader.readAsDataURL(file);
    } catch (error) {
      errorHandler(error, "Error downloading run sheet");

      return;
    }
    onClose?.();
  };

  return (
    <div className="DownloadFileCotainer flex flex-col flex-1">
      <div className="DownloadFileContent flex-1 flex flex-col">
        <p>Run Sheet</p>
        <div>
          <strong>Sheet kind</strong>
        </div>
        <div>
          <div className="InlineTabsWrapper InlineTabsWrapperStart">
            <ToggleButton
              text1="Short run sheet"
              text2="Custom run sheet"
              selected={false}
              onClick={handleSelectCustomRunSheet}
            />
          </div>
        </div>
        <div>
          <strong>File type</strong>
        </div>
        <div>
          <div className="InlineTabsWrapper InlineTabsWrapperStart">
            <ToggleButton
              text1="PDF"
              text2="Excel/CSV"
              selected={fileType === "pdf"}
              onClick={() => {
                setFileType(fileType === "pdf" ? "csv" : "pdf");
              }}
            />
          </div>
        </div>
        <div className="SelectList m-2.5">
          <button
            type="button"
            className="SelectListItem my-5 text-white text-[15px] font-normal leading-[19px] border-b border-[#1e2228] pb-5 w-full flex justify-start"
            onClick={() => {}}
          >
            <div className="MultiRow flex-1 flex flex-col items-start justify-start">
              <div>Bookings</div>
              <div>
                <small>Unassinged, Guest List</small>
              </div>
            </div>
            <div className="flex-none justify-end flex items-center">
              {" "}
              <ChevronRight className="size-5" />
            </div>
          </button>
          <button
            type="button"
            className="SelectListItem my-5 text-white text-[15px] font-normal leading-[19px] border-b border-[#1e2228] pb-5 w-full flex justify-start"
            onClick={() => {}}
          >
            <div className="MultiRow flex-1 flex flex-col items-start justify-start">
              <div>Include details </div>
              <div>
                <small>Phone, Booking, reference, Source </small>
              </div>
            </div>
            <div className="flex-none justify-end flex items-center">
              <ChevronRight className="size-5" />
            </div>
          </button>
          <button
            type="button"
            className="SelectListItem my-5 text-white text-[15px] font-normal leading-[19px] border-b border-[#1e2228] pb-5 w-full flex justify-start"
            onClick={() => {}}
          >
            <div className="MultiRow flex-1 flex flex-col items-start justify-start">
              <div>Extra details </div>
              <div>
                <small>
                  Special Requests, Custom field , Labels, Run sheet notes,
                  Pre-orders, Payments{" "}
                </small>
              </div>
            </div>
            <div className="flex-none justify-end flex items-center">
              <ChevronRight className="size-5" />
            </div>
          </button>
          <div>
            <strong>Zone</strong>
          </div>
          <div>
            <Select onValueChange={(value) => console.log(value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select zone" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All zones</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <hr />
          <div>
            <strong>Sort</strong>
          </div>
          <div>
            <Select onValueChange={(value) => console.log(value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select sort order" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="time">By time</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
      <div className="DownloadFileFooter flex-none flex flex-row gap-2.5">
        <Button className="DownloadFileFooterButton" onClick={handleBack}>
          Back
        </Button>
        <Button
          className="DownloadFileFooterButton"
          onClick={handleDownload}
          type="primary"
        >
          Download Run Sheet
        </Button>
      </div>
    </div>
  );
};
